import { useDingerTime } from "../../hooks/useDingerTime";
import { useScheduleWeather } from "../../hooks/useScheduleWeather";
import type { TeamAppModel } from "../../hooks/teamTypes";
import { AppShell } from "./AppShell";
import { ManagementPanelAdapter } from "./ManagementPanelAdapter";
import { TeamRouteContent } from "./TeamRouteContent";
import { actionKeys } from "../../hooks/actionKeys";

interface TeamScreenRouterProps {
  model: TeamAppModel;
}

export function TeamScreenRouter({ model }: TeamScreenRouterProps) {
  const { shell, setup, game, weatherEntries, setupWeatherId } = model;
  const scheduleWeather = useScheduleWeather({
    enabled: shell.screen === "schedule" || shell.screen === "setup",
    entries: weatherEntries.filter(
      (entry) => entry.seriesAvailability !== "not_needed",
    ),
  });
  const dinger = useDingerTime({
    enabled: setup.game !== null,
    field: setup.game?.field ?? null,
  });
  const setupWeatherState = setupWeatherId
    ? scheduleWeather.forecastsByEntryId[setupWeatherId]
    : undefined;
  const mercyRule =
    game.mercyRulePending &&
    game.mercyRuleSide !== null &&
    game.mercyRuleRunsRemaining !== null
      ? {
          side: game.mercyRuleSide,
          runsRemaining: game.mercyRuleRunsRemaining,
          runsNeededToPrevent: game.mercyRuleRunsNeededToPrevent,
        }
      : null;

  return (
    <AppShell
      screen={shell.screen}
      teamName={shell.teamName}
      hasActiveGame={shell.hasActiveGame}
      hasActiveSeason={!!shell.activeSeasonId}
      opponentName={shell.opponentName}
      inning={shell.inning}
      batting={shell.batting}
      ourScore={shell.ourScore}
      opponentScore={shell.opponentScore}
      connectionState={shell.connectionState}
      onRetryUnknown={shell.retryUnknown}
      isScorer={shell.isScorer}
      scorerName={shell.scorerName}
      scoringClaimed={shell.scoringClaimed}
      dingerTime={dinger.conditions}
      mercyRule={mercyRule}
      onClaimScoring={shell.claimScoring}
      claimScoringPending={shell.actionStatus.isPending(
        actionKeys.claimScoring,
      )}
      claimScoringUnknown={
        shell.actionStatus.phaseFor(actionKeys.claimScoring) ===
        "outcome_unknown"
      }
      onNavigate={(nextScreen) =>
        shell.setScreen(
          nextScreen === "stats" && !shell.hasActiveGame
            ? "season"
            : nextScreen,
        )
      }
      manageOpen={shell.manageOpen}
      managementContent={
        <ManagementPanelAdapter management={model.management} />
      }
      onManageOpenChange={shell.setManageOpen}
      notification={shell.toast}
    >
      <TeamRouteContent
        model={model}
        weatherByEntryId={scheduleWeather.forecastsByEntryId}
        {...(setupWeatherState ? { setupWeatherState } : {})}
        onRetryWeather={scheduleWeather.retry}
        weatherRefreshing={scheduleWeather.refreshing}
        onPrint={() => window.print()}
      />
    </AppShell>
  );
}
